/**
 * @fileoverview Syndicator Showdown — Share QR Modal
 * @module components/share/ShareQrModal
 *
 * Unified share modal (Phase 6) used by every share surface — invite links,
 * stable links, result cards, and (in future) leaderboard rank shares.
 * Rendered through a React portal directly under <body> (same pattern as
 * BuyModal/ReviewSlip) so it is never clipped or mis-stacked by an
 * ancestor's overflow/transform.
 *
 * Accepts an optional `previewComponent` (e.g. <ShareStableCard/> or
 * <ShareResultCard/>) rendered above the QR code so every share flow can
 * show a real, on-brand preview of what's being shared — not just a plain
 * link. When a `shareId` is supplied, Copy/Share/QR-open actions are
 * recorded via shareTrackingService (best-effort, never blocks the UI).
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { copyToClipboard, canNativeShare, nativeShare } from '../../utils/shareLinks';
import { trackShareEvent } from '../../services/shareTrackingService';
import './ShareQrModal.css';

export default function ShareQrModal({
  isOpen,
  onClose,
  url,
  shareUrl,
  title = 'Share',
  description,
  subtitle,
  shareTitle,
  shareText,
  qrLabel,
  previewComponent = null,
  footerNote = null,
  shareId = null,
}) {
  const [copied, setCopied] = useState(false);
  const resolvedUrl = url || shareUrl;
  const resolvedDescription = description || subtitle;
  const qrTrackedRef = useRef(null);

  // Body scroll lock while open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Escape key closes
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Reset the "Copied!" state whenever the modal re-opens, and record a
  // single qr_open event per open (the QR code is always visible once the
  // modal is up, so "opening the modal" is the meaningful QR event here).
  useEffect(() => {
    if (isOpen) {
      setCopied(false);
      if (shareId && qrTrackedRef.current !== shareId) {
        qrTrackedRef.current = shareId;
        trackShareEvent(shareId, 'qr_open');
      }
    }
  }, [isOpen, shareId]);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(resolvedUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      if (shareId) trackShareEvent(shareId, 'copy');
    }
  }, [resolvedUrl, shareId]);

  const handleShare = useCallback(async () => {
    const shared = await nativeShare({ title: shareTitle, text: shareText, url: resolvedUrl });
    if (!shared) { handleCopy(); return; }
    if (shareId) trackShareEvent(shareId, 'native_share');
  }, [resolvedUrl, shareTitle, shareText, handleCopy, shareId]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="sqm-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="sqm-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="sqm-header">
          <h2 className="sqm-title">{title}</h2>
          <button
            type="button"
            className="sqm-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="sqm-body">
          {resolvedDescription && <p className="sqm-description">{resolvedDescription}</p>}

          {previewComponent && (
            <div className="sqm-preview-wrap">{previewComponent}</div>
          )}

          {resolvedUrl ? (
            <>
              <div className="sqm-qr-wrap">
                <div className="sqm-qr-card">
                  <QRCodeSVG value={resolvedUrl} size={176} level="M" marginSize={2} />
                </div>
                {qrLabel && <span className="sqm-qr-label">{qrLabel}</span>}
              </div>

              <div className="sqm-url-row">
                <span className="sqm-url-text" title={resolvedUrl}>{resolvedUrl}</span>
              </div>

              <div className="sqm-actions">
                <button type="button" className="sqm-btn sqm-btn--primary" onClick={handleCopy}>
                  {copied ? '✓ Link Copied' : 'Copy Link'}
                </button>
                {canNativeShare() && (
                  <button type="button" className="sqm-btn sqm-btn--secondary" onClick={handleShare}>
                    Share Link
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="sqm-loading"><div className="ss-spinner" /></div>
          )}

          {footerNote && <div className="sqm-footer-note">{footerNote}</div>}
        </div>
      </div>
    </div>,
    document.body
  );
}
